// ExchangeStageCreate.jsx
import React, {useMemo, useState} from "react";
import {Alert, App, Button, Descriptions, Tag, Typography} from "antd";
import Shell from "../../exchange/v1/ui/Shell";
import api from "../../../api/axiosClient";
import {formatUtc} from "./utils/time";

const {Text} = Typography;

const API = {
  transaction: (id) => `/api/exh/order-transactions/${id}/`,
};

function normalizeStatus(s) {
  return String(s || "").toUpperCase();
}

function statusTag(status) {
  const s = normalizeStatus(status);
  if (s === "DISPUTE") return <Tag color="red">СПОР</Tag>;
  if (s === "CANCELLED" || s === "CANCELED") return <Tag>ОТМЕНЕНА</Tag>;
  if (s === "COMPLETED" || s === "DONE") return <Tag color="green">ЗАВЕРШЕНА</Tag>;
  if (s === "IN_PROGRESS" || s === "IN_PROCESS") return <Tag color="gold">В ПРОЦЕССЕ</Tag>;
  if (s === "CREATED") return <Tag color="blue">СОЗДАНА</Tag>;
  return <Tag>{s || "—"}</Tag>;
}

export default function ExchangeStageCreate({stage, tx, onBack, onCancelled}) {
  const {modal, message} = App.useApp();
  const [cancelLoading, setCancelLoading] = useState(false);

  const canCancel = useMemo(() => normalizeStatus(tx?.status) === "CREATED", [tx]);

  const rateValue = (() => {
    const num = (v) => {
      const n = typeof v === "number" ? v : Number(String(v ?? "").replace(",", "."));
      return Number.isFinite(n) ? n : NaN;
    };

    const direct = num(tx?.rate ?? tx?.exchange_rate ?? null);
    if (Number.isFinite(direct) && direct > 0) return direct;

    const buy = num(tx?.pair?.buy_rate ?? null);
    const ratio = num(tx?.pair?.ratio_rate ?? 1);
    if (Number.isFinite(buy) && buy > 0 && Number.isFinite(ratio) && ratio > 0) return buy * ratio;

    const af = num(tx?.amount_from ?? null);
    const at = num(tx?.amount_to ?? null);
    if (Number.isFinite(af) && af > 0 && Number.isFinite(at) && at > 0) return at / af;

    return null;
  })();

  const rateText = rateValue
    ? `1 ${tx?.currency_from ?? tx?.pair?.currency_from ?? "—"} = ${rateValue.toFixed(6)} ${
      tx?.currency_to ?? tx?.pair?.currency_to ?? "—"
    }`
    : "—";

  const handleCancel = () => {
    if (cancelLoading) return;
    if (!tx?.id) return;

    modal.confirm({
      title: "Отменить заявку?",
      content: "Заявка будет переведена в статус «Отменена». Продолжить?",
      okText: "Да, отменить",
      cancelText: "Нет",
      okButtonProps: {danger: true},
      onOk: async () => {
        try {
          setCancelLoading(true);

          const payload = {status: "CANCELLED"};
          const res = await api.patch(API.transaction(tx.id), payload);

          message.success("Заявка отменена");

          if (typeof onCancelled === "function") {
            onCancelled(res?.data || {...tx, ...payload});
          } else if (typeof onBack === "function") {
            onBack();
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(e);
          const data = e?.response?.data;
          const msg =
            (typeof data === "string" && data) || data?.detail || "Не удалось отменить заявку";
          message.error(msg);
        } finally {
          setCancelLoading(false);
        }
      },
    });
  };

  return (
    <Shell
      stage={stage}
      title="Поиск встречной стороны"
      subtitle="Этап 2. Ожидание подбора контрагента."
      minBodyHeight={520}
    >
      <div className="mt-2">
        <Alert
        type="warning"
        showIcon
        className="uppercase font-bold mb-2"
        message="Важно!"
        description="После начала обмена у вас будет 2 часа на выполнение всех переводов и подтверждений."
      />
        <Alert
          type="info"
          showIcon
          message="Идёт поиск контрагента"
          description="Заявка создана. Система подбирает контрагента и проверяет ликвидность."
        />
      </div>

      <div className="mt-6">
        <Descriptions size="small" bordered column={1} labelStyle={{width: 220}}>
          <Descriptions.Item label="ID заявки">{tx?.id || "—"}</Descriptions.Item>

          <Descriptions.Item label="Статус">
            <div className="flex items-center gap-2">
              {statusTag(tx?.status)}
              <Text type="secondary">{normalizeStatus(tx?.status)}</Text>
            </div>
          </Descriptions.Item>

          <Descriptions.Item label="Отдаёте">
            {tx?.amount_from ?? "—"} {tx?.currency_from ?? tx?.pair?.currency_from ?? "—"}
          </Descriptions.Item>

          <Descriptions.Item label="Получаете">
            {tx?.amount_to ?? "—"} {tx?.currency_to ?? tx?.pair?.currency_to ?? "—"}
          </Descriptions.Item>

          <Descriptions.Item label="Валютная пара">
            {tx?.pair?.currency_from || "—"} → {tx?.pair?.currency_to || "—"}
          </Descriptions.Item>

          <Descriptions.Item label="Курс">{rateText}</Descriptions.Item>

          <Descriptions.Item label="Реквизиты">
            {tx?.payout?.detail || "—"} ({tx?.pair?.method_to?.title || "—"})
          </Descriptions.Item>

          <Descriptions.Item label="Создана">
            {tx?.created_at ? formatUtc(tx.created_at) : "—"}
          </Descriptions.Item>
        </Descriptions>
      </div>

      <div className="mt-6 text-sm text-gray-500">
        Пока заявка находится в статусе <b className="mx-1">CREATED</b>, вы можете отменить её.
      </div>

      <div className="mt-8 flex justify-start">
        <Button danger onClick={handleCancel} loading={cancelLoading} disabled={!canCancel}>
          Отменить заявку
        </Button>
      </div>
    </Shell>
  );
}